import React, { useMemo, useState } from "react";
import { getPuzzlePieceImage, puzzleSize, PUZZLE_BLEED_PERCENT, PUZZLE_PIECE_OVERLAP } from "@/config/puzzleConfig";
import { cn } from "@/lib/utils";
import {
  isActivatableModuleState,
  PackageModuleInfo,
  PackageModuleState,
} from "@/services/student/packages.interfaces";

type ModuleState = PackageModuleState;

interface PieceProps {
  id: number;
  posX: number;
  posY: number;
  data: PackageModuleInfo | null;
  moduleState: ModuleState;
  onActivate: (data: PackageModuleInfo) => void;
  onMouseEnter?: (data: PackageModuleInfo) => void;
  onMouseLeave?: () => void;
}

// Opacity per state — higher = more "fog" (locked), lower = landscape revealed
const STATE_OPACITY: Record<ModuleState, number> = {
  not_available: 0.9,
  needs_purchase: 0.42,
  available:     0.32,
  "in-progress": 0.18,
  completed:     0.05,
};

const HOVER_OPACITY = 0.82;

// CSS filter per state
const STATE_FILTER: Record<ModuleState, string> = {
  not_available: "grayscale(1) brightness(2.2)",
  needs_purchase: "grayscale(0.15) brightness(1.05) saturate(1.25)",
  available:     "none",
  "in-progress": "brightness(1.10) saturate(1.15)",
  completed:     "brightness(1.20) saturate(1.30)",
};

// Piece shape via CSS clip-path polygon — same deterministic logic as before
function shouldHaveBump(x: number, y: number, side: string): boolean {
  const seed = x * 1000 + y * 10000 + (side === "right" ? 1 : 2);
  return ((Math.sin(seed) + 1) / 2) > 0.5;
}

function getClipPath(edges: {
  hasTopBump: boolean;
  hasRightBump: boolean;
  hasBottomBump: boolean;
  hasLeftBump: boolean;
}): string {
  const bumpSize = 25;
  const bumpWidth = 15;
  const pts: string[] = [];

  pts.push("0% 0%");
  if (edges.hasTopBump) {
    pts.push(
      `${35 - bumpWidth / 2}% 0%`,
      `35% ${-bumpSize / 3}%`,
      `50% ${-bumpSize}%`,
      `65% ${-bumpSize / 3}%`,
      `${65 + bumpWidth / 2}% 0%`,
    );
  }
  pts.push("100% 0%");
  if (edges.hasRightBump) {
    pts.push(
      `100% ${35 - bumpWidth / 2}%`,
      `${100 + bumpSize / 3}% 35%`,
      `${100 + bumpSize}% 50%`,
      `${100 + bumpSize / 3}% 65%`,
      `100% ${65 + bumpWidth / 2}%`,
    );
  }
  pts.push("100% 100%");
  if (edges.hasBottomBump) {
    pts.push(
      `${65 + bumpWidth / 2}% 100%`,
      `65% ${100 + bumpSize / 3}%`,
      `50% ${100 + bumpSize}%`,
      `35% ${100 + bumpSize / 3}%`,
      `${35 - bumpWidth / 2}% 100%`,
    );
  }
  pts.push("0% 100%");
  if (edges.hasLeftBump) {
    pts.push(
      `0% ${65 + bumpWidth / 2}%`,
      `${-bumpSize / 3}% 65%`,
      `${-bumpSize}% 50%`,
      `${-bumpSize / 3}% 35%`,
      `0% ${35 - bumpWidth / 2}%`,
    );
  }

  return `polygon(${pts.join(", ")})`;
}

const PuzzlePiece = ({
  id,
  posX,
  posY,
  data,
  moduleState,
  onActivate,
  onMouseEnter,
  onMouseLeave,
}: PieceProps) => {
  const [isHover, setIsHover] = useState(false);

  const isActive = isActivatableModuleState(moduleState);
  // Outline SVGs are hollow — opacity only reads as fog on filled artwork.
  const isFilled = true;

  // Grid spans past the frame on every side so internal SVG artwork fills edge-to-edge.
  const widthPercent = (PUZZLE_PIECE_OVERLAP / puzzleSize.hor) * 100;
  const heightPercent = (PUZZLE_PIECE_OVERLAP / puzzleSize.vert) * 100;
  const xPercent = (posX / puzzleSize.hor) * 100;
  const yPercent = (posY / puzzleSize.vert) * 100;

  const image = useMemo(
    () => getPuzzlePieceImage(posX, posY, isFilled),
    [posX, posY, isFilled],
  );

  const pieceEdges = useMemo(() => ({
    hasTopBump:    posY > 0 ? !shouldHaveBump(posX, posY - 1, "bottom") : false,
    hasRightBump:  posX < puzzleSize.hor - 1 ? shouldHaveBump(posX, posY, "right") : false,
    hasBottomBump: posY < puzzleSize.vert - 1 ? shouldHaveBump(posX, posY, "bottom") : false,
    hasLeftBump:   posX > 0 ? !shouldHaveBump(posX - 1, posY, "right") : false,
  }), [posX, posY]);

  const imageOpacity = isHover && isActive
    ? HOVER_OPACITY
    : STATE_OPACITY[moduleState];

  const imageFilter = isHover && isActive
    ? "brightness(1.18)"
    : STATE_FILTER[moduleState];

  const handleMouseEnter = () => {
    if (!isActive) return;
    setIsHover(true);
    if (onMouseEnter && data) onMouseEnter(data);
  };

  const handleMouseLeave = () => {
    if (!isActive) return;
    setIsHover(false);
    if (onMouseLeave) onMouseLeave();
  };

  const handleClick = () => {
    if (!isActive || !data) return;
    onActivate(data);
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role={isActive ? "button" : "presentation"}
      aria-label={
        isActive && data
          ? moduleState === "needs_purchase"
            ? `Comprar: ${data.name}`
            : `Módulo: ${data.name}`
          : undefined
      }
      className={cn(
        "absolute puzzle-piece-enter",
        isActive && "cursor-pointer",
        isHover ? "z-30" : isActive ? "z-20" : "z-10",
      )}
      style={{
        left: `${xPercent - PUZZLE_BLEED_PERCENT}%`,
        top: `${yPercent - PUZZLE_BLEED_PERCENT}%`,
        width: `${widthPercent}%`,
        height: `${heightPercent}%`,
        animationDelay: `calc(${id} * 11ms)`,
        // Hover expand — spring in (overshoot), ease-out on leave
        transform: isHover ? "scale(1.22)" : "scale(1)",
        transition: isHover
          ? "transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 220ms ease-out"
          : "transform 240ms cubic-bezier(0.25, 0, 0, 1), box-shadow 200ms ease-out",
        boxShadow: isHover && isActive
          ? "0 12px 32px -4px rgba(0,0,0,0.38), 0 6px 12px -2px rgba(0,0,0,0.22)"
          : "none",
      }}
    >
      <img
        src={image}
        alt=""
        aria-hidden="true"
        className="w-full h-full"
        style={{
          objectFit: "cover",
          opacity: imageOpacity,
          filter: imageFilter,
          clipPath: getClipPath(pieceEdges),
          transition: "opacity 200ms ease-out, filter 200ms ease-out",
        }}
      />

      {/* Label centered in the piece — clip-path is on img only, so this is never clipped */}
      {isHover && isActive && data?.name && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50 p-1">
          <span
            className="bg-gray-900/80 text-white text-sm font-semibold leading-snug px-2.5 py-1.5 rounded-md text-center backdrop-blur-sm"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              maxWidth: "100%",
            }}
          >
            {data.name}
          </span>
        </div>
      )}
    </div>
  );
};

export default PuzzlePiece;
